import { toUpper } from "lodash";
import countries from "i18n-iso-countries";
import states from "./states.json";
import { isNumberNotEmpty, isStringNotEmpty } from "./objectUtils";
import { toAgeAsNumber } from "./dateFormatter";

export const doesStringContainNumbers = (value) => {
    const regEx = /\d/;

    return regEx.test(value);
};

export const checkStringLength = (value, len) => {
    if (isStringNotEmpty(value)) {
        return value.length >= len;
    }

    return false;
};

export const checkStringMaxLength = (value, len) => {
    if (isStringNotEmpty(value)) {
        return value.length <= len;
    }

    return false;
};

// checks that all the letters in the string are uppercase
export const isUpperCase = (str) => {
    if (isStringNotEmpty(str)) {
        return str === str.toUpperCase();
    }

    return false;
};

export const isValidPhoneForE164 = (phoneNumber) => {
    const regEx = /^\+[1-9]\d{10,14}$/;

    return regEx.test(phoneNumber);
};

// Single source of truth for email format — local part allows + and % (e.g. user+tag@domain.com).
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@([\w-]+\.)+[\w-]+$/i;

// Variant for react-hook-form pattern rules: also passes empty values and the literal
// "undefined" so optional/unset fields don't trip the pattern error.
export const OPTIONAL_EMAIL_REGEX =
    process.env.REACT_APP_FIREBASE_PROJECT_ID === 'ready-rx-cd342'
        ? /^undefined|^$|^[a-zA-Z0-9._%+-]+@([\w-]+\.)+[\w-]+$/i
        // Allow `=` in non-PROD environments for sign in via base64 encoded email.
        // the hyphen stays last so it is a literal, not a `+`-to-`=` range.
        : /^undefined|^$|^[a-zA-Z0-9._%+=-]+@([\w-]+\.)+[\w-]+$/i;

// OPTIONAL_EMAIL_REGEX without its empty and "undefined" escape hatches, for a sign-in identifier
// that must be an address - including the base64 addresses UAT signs in with
export const SIGN_IN_EMAIL_REGEX =
    process.env.REACT_APP_FIREBASE_PROJECT_ID === 'ready-rx-cd342'
        ? EMAIL_REGEX
        : /^[a-zA-Z0-9._%+=-]+@([\w-]+\.)+[\w-]+$/i;

export const isValidEmail = (email) => {
    return OPTIONAL_EMAIL_REGEX.test(email);
};


export const isValidStateAbbreviation = (twoLetterStateAbbreviation) => {
    return states.some(s => toUpper(s.abbreviation) === toUpper(twoLetterStateAbbreviation));
};

export const isValid3LetterCountryCode = (threeLetterCountryCode = '') => {
    return countries.isValid(threeLetterCountryCode) && threeLetterCountryCode.length === 3;
};

export const isValid5or9ZipCode = (zipCode) => {
    if (isStringNotEmpty(zipCode)) {
        const regEx = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

        return regEx.test(zipCode);
    } else {
        return true;
    }
};

// Fallback for the authoritative USPS check (Smarty candidate metadata.recordType === 'P')
// when no address-verification round-trip has happened — hand-typed fields, "same as
// billing" copies, or a failed lookup. Word boundaries
// keep real street names such as Boxwood Ln, Box Elder Rd and Post Rd from matching.
// PMB is deliberately absent: a private mail box is a street address that UPS delivers to,
// and USPS does not flag it as a PO Box either.
export const isPOBoxAddress = (streetAddress) => {
    if (isStringNotEmpty(streetAddress)) {
        const regEx = /\b(?:p[.\s-]*o[.\s-]*(?:box|b\b)|post\s*office\s+box|postal\s+box)\b/i;

        return regEx.test(streetAddress);
    }

    return false;
};

export const isValidMBI = (mbi) => {
    const regEx = /(^[1-9][^SLOIBZsloibz0-9][^SLOIBZsloibz][0-9]-?[^SLOIBZsloibz0-9][^SLOIBZsloibz][0-9]-?[^SLOIBZsloibz0-9][^SLOIBZsloibz0-9][0-9][0-9]$)/;

    return regEx.test(mbi);
};

// https://www.scaler.com/topics/date-validation-in-javascript/
export const isValidDate = (value) => {
    if (isStringNotEmpty(value) && value !== 'MM/DD/YYYY') {
        let dateformat = /^(0?[1-9]|1[0-2])[\/](0?[1-9]|[1-2][0-9]|3[01])[\/]\d{4}$/;

        // Matching the date through regular expression      
        if (value.match(dateformat)) {
            let operator = value.split('/');

            // Extract the string into month, date and year      
            let datepart = [];
            if (operator.length > 1) {
                datepart = value.split('/');
            }
            let month = parseInt(datepart[0]);
            let day = parseInt(datepart[1]);
            let year = parseInt(datepart[2]);

            if (day > 31 || day < 1) {
                return false;
            }

            let currentYear = new Date().getFullYear();
            if (year < 1900 || year > (currentYear + 5)) {
                return false;
            }

            // Create a list of days of a month      
            let ListofDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
            if (month === 1 || month > 2) {
                if (day > ListofDays[month - 1]) {
                    // to check if the date is out of range     
                    return false;
                }
            } else if (month === 2) {
                let leapYear = false;
                if ((!(year % 4) && year % 100) || !(year % 400)) leapYear = true;
                if ((leapYear === false) && (day >= 29)) return false;
                else
                    if ((leapYear === true) && (day > 29)) {
                        // console.log('Invalid date format!');
                        return false;
                    }
            } else if (month > 12 || month < 1) {
                return false;
            }
        } else {
            // console.log("Invalid date format!");
            return false;
        }
    }
    return true;
};

export const isValidTime = (value) => {
    if (isStringNotEmpty(value)) {
        let timeformat = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;

        // Matching the date through regular expression      
        if (value.match(timeformat)) {
            return true;
        } else {
            // console.log("Invalid time format!");
            return false;
        }
    }
    return true;
}

export const isAgeUnder18 = (dateOfBirth) => {
    let age = toAgeAsNumber(dateOfBirth);
    // console.log('AGE: ', age);
    if (isNumberNotEmpty(age)) {
        return age < 18;
    }

    // if the age cannot be parsed then default to false
    return false;
};

export const isAgeUnder20 = (dateOfBirth) => {
    let age = toAgeAsNumber(dateOfBirth);
    // console.log('AGE: ', age);
    if (isNumberNotEmpty(age)) {
        return age < 20;
    }

    // if the age cannot be parsed then default to false
    return false;
};
/**
 * Gender is only ever MALE or FEMALE, or absent.
 *
 * Absent is honest - it says the customer has not answered, and every consumer reads it that way. A third
 * value is not: 'UNKNOWN' satisfies isStringNotEmpty, so it travels into Customer, Lead and the encounter
 * and reads downstream as a stated fact, and the meal-plan generator interpolates it into its prompt.
 * Mirrors the backend helper of the same name.
 */
const WRITABLE_GENDERS = ['MALE', 'FEMALE'];

export const isWritableGender = (value) => WRITABLE_GENDERS.includes(value);

// Normalizes case. This is DEFENSIVE, not corrective: no code path in this repository can write a
// lowercase gender. Every value originates from `Enums.Genders` keys (`MALE`/`FEMALE`/`UNKNOWN`) via
// `FormItemGender` or the funnel's gender step, and the only `toLower(gender)` calls in the tree are
// outbound Klarna payloads that never write back. Verified 2026-09-07.
//
// Kept because it cannot do harm: it only canonicalises a value that is already MALE or FEMALE in some
// case, and anything that is not a gender is still refused. Do NOT treat it as evidence that such data
// exists, and do not build behavior on the assumption that it does.
export const toWritableGender = (value) => ((typeof value === 'string' && WRITABLE_GENDERS.includes(value.toUpperCase())) ? value.toUpperCase() : null);
